import { useCallback, useEffect, useRef } from 'react'
import { omit } from '../../../utils/object'
import { addDatadogRumError } from '../../rum/DataDogRum'

export const mergeOptions = (prevOptions, newOptions) => {
  const toOmit = []
  if (newOptions.bounds) toOmit.push('center', 'zoom')
  if (newOptions.center) toOmit.push('bounds')
  return omit(
    {
      ...(prevOptions || {}),
      ...newOptions,
      ...(prevOptions?.animate !== undefined || newOptions?.animate !== undefined
        ? { animate: Boolean(prevOptions?.animate || newOptions?.animate) }
        : {})
    },
    toOmit
  )
}

const mergeEventData = (prevEventData, newEventData) => ({ ...prevEventData, ...newEventData })

const setPaddingWithoutVisibleMove = (map, padding) => {
  if (!map) return
  map.stop()
  const currentPadding = map.getPadding()
  const xyCenter = map.project(map.getCenter())
  const deltaX = (currentPadding.left - padding.left - (currentPadding.right - padding.right)) / 2
  const deltaY = (currentPadding.top - padding.top - (currentPadding.bottom - padding.bottom)) / 2
  const center = map.unproject({
    x: xyCenter.x - deltaX,
    y: xyCenter.y - deltaY
  })
  map.flyTo({ center, padding, animate: false })
}

const executeInteraction = (map, options, eventData) => {
  if (!options) {
    const err = new Error('options undefined in useMapBoundsInteraction.executeInteraction')
    addDatadogRumError(err)
    return
  }
  if (options.bounds) {
    if (options.padding) setPaddingWithoutVisibleMove(map, options.padding)
    map.fitBounds(options.bounds, omit(options, ['bounds', 'padding']), eventData)
  } else {
    map.flyTo(options, eventData)
  }
}

const useMapBoundsInteraction = map => {
  const waitingOptionsRef = useRef({})
  const wantedPadding = useRef(null)
  const moveEndRef = useRef()

  useEffect(() => {
    if (!map) return
    moveEndRef.current = () => {
      waitingOptionsRef.current = {}
    }
  }, [map])

  return useCallback(
    (options, eventData = {}) => {
      if (!map || !options) return

      if (moveEndRef.current) map.off('moveend', moveEndRef.current)

      waitingOptionsRef.current = {
        options: mergeOptions(waitingOptionsRef.current?.options, options),
        eventData: mergeEventData(waitingOptionsRef.current?.eventData, eventData)
      }
      if (waitingOptionsRef.current?.options.padding) {
        wantedPadding.padding = waitingOptionsRef.current?.options.padding
      } else if (wantedPadding.padding) {
        waitingOptionsRef.current.options.padding = wantedPadding.padding
      }

      executeInteraction(map, waitingOptionsRef.current.options, waitingOptionsRef.current.eventData)
      map.once('moveend', moveEndRef.current)
    },
    [map]
  )
}

export default useMapBoundsInteraction
